"use client";

import React, { useState } from "react";
import Image from "next/image";
import BannerImages from "@/components/newPageComponent/cpa-new/visit/BannerImages";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import EnqueryForm from "@/components/forms/EnqueryForm";
import { usePathname } from "next/navigation";

interface VisitPageBannerProps {
  title: string;
  pageName: string;
  bulletPoints: string[];
  isWebinarPage?: boolean;
}

const VisitPageBannerNew: React.FC<VisitPageBannerProps> = ({
  title,
  pageName,
  bulletPoints,
  isWebinarPage,
}) => {
  const pathname = usePathname();
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);

  // Check if current page is the special fees page
  const isFeesPage =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  // Check if current page is the CMA landing page
  const isCmaLandingPage = pathname === "/best-us-cma-coaching-in-india";

  const handleFormModelOpen = () => {
    sessionStorage.setItem("modal-open", "true");
    setIsFormModalOpen(true);
  };

  const handleFormModelClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  };

  useModalScrollLock(isFormModalOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  return (
    <>
      <div className="hidden xl:block 2xl:hidden">
        {isFeesPage ? (
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma_banner_desktop.webp"
            alt="CPA Banner"
            width={1200}
            height={600}
            className="w-full h-auto cursor-pointer py-2"
            onClick={handleFormModelOpen}
          />
        ) : isCmaLandingPage ? (
          <Image
            src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma_banner_desktop.webp"
            alt="CMA Banner"
            width={1200}
            height={600}
            className="w-full h-auto cursor-pointer py-2"
            onClick={handleFormModelOpen}
          />
        ) : (
          <BannerImages pageName={pageName} onClick={handleFormModelOpen} />
        )}
      </div>
      <section
        className="relative w-full"
        style={{
          background:
            "linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.61) 78.95%, #39F 153.47%), linear-gradient(0deg, #FFF 0%, #FFF 100%), linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.61) 78.95%, #39F 153.47%)",
        }}
      >
        <div className="main_container mx-auto px-5 pb-8 pt-4 sm:pb-12 lg:pb-16 sm:pt-0 lg:pt-0">
          <div className="hidden xl:hidden 2xl:block">
            {isFeesPage ? (
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/Top_Banner_CPA-Desktop.webp"
                alt="CPA Banner"
                width={1200}
                height={600}
                className="w-full h-auto cursor-pointer py-2"
                onClick={handleFormModelOpen}
              />
            ) : isCmaLandingPage ? (
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma_banner_desktop.webp"
                alt="CMA Banner"
                width={1200}
                height={600}
                className="w-full h-auto cursor-pointer py-2"
                onClick={handleFormModelOpen}
              />
            ) : (
              <BannerImages pageName={pageName} onClick={handleFormModelOpen} />
            )}
          </div>
          <div className="flex flex-col lg:flex-row items-center lg:items-center justify-between gap-2 lg:gap-10 relative">
            <div className="md:hidden block w-full py-4">
              {isFeesPage ? (
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma_banner_mobile.webp"
                  alt="CPA Banner"
                  width={600}
                  height={400}
                  className="w-full h-auto cursor-pointer"
                  onClick={handleFormModelOpen}
                />
              ) : isCmaLandingPage ? (
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/landing-pages/cma_banner_mobile.webp"
                  alt="CMA Banner"
                  width={600}
                  height={400}
                  className="w-full h-auto cursor-pointer"
                  onClick={handleFormModelOpen}
                />
              ) : (
                <BannerImages
                  pageName={pageName}
                  onClick={handleFormModelOpen}
                />
              )}
            </div>
            <div className="w-full items-center lg:items-start flex lg:flex-col flex-col-reverse lg:w-7/12 relative lg:top-10 mb-5">
              {isCmaLandingPage ? (
                <div>
                  {/* Desktop Version */}
                  <div className="hidden md:block">
                    <h1 className="md:mt-0 md:leading-[44px] font-semibold text-[#061A2F] text-2xl sm:text-[24px] lg:text-[44px] md:text-left text-center leading-6">
                      Be an AI-Ready CMA in 2026
                      <br />
                      <span className="md:text-[36px] text-lg font-semibold leading-normal tracking-[-1.44px] bg-[linear-gradient(88deg,_#39F_73.02%,_#FFB801_90.73%)] bg-clip-text text-transparent relative">
                        Earn 50L+
                        <sup className="bg-[linear-gradient(88deg,_#39F_53.02%,_#FFB801_80.73%)] bg-clip-text text-transparent md:text-lg absolute top-[-5px]">
                          *
                        </sup>{" "}
                        per annum
                      </span>
                    </h1>
                    <div className="md:mt-4 mt-2 text-[#061A2F] font-medium text-base flex flex-col gap-1 md:leading-[25px] md:items-start">
                      <p>2 Exams | 8 Months</p>
                      <p>Jobs at Big 4 & MNCs in India & U.S.</p>
                    </div>
                    {/* Desktop Stats Cards */}
                    <div className="mt-6 grid grid-cols-3 gap-4">
                      <div className="rounded-[12px] border border-[#E5E5E5] bg-white p-[10px] text-center">
                        <p className="text-black md:text-base text-sm font-normal md:leading-5">
                          India's #1{" "}
                          <span className="text-[#39F] font-bold md:text-[20px]">
                            CMA
                          </span>
                          <br />
                          Institute
                        </p>
                      </div>
                      <div className="rounded-[12px] border border-[#E5E5E5] bg-white p-[10px] text-center">
                        <p className="text-[#39F] md:text-[20px] font-bold md:leading-5">
                          70K+
                        </p>
                        <p className="text-black md:text-base text-sm font-normal md:leading-5">
                          Global Alumni
                        </p>
                      </div>
                      <div className="rounded-[12px] border border-[#E5E5E5] bg-white p-[10px] text-center">
                        <p className="text-[#39F] md:text-[20px] font-bold md:leading-5">
                          1000s{" "}
                          <span className="text-black md:text-base text-sm font-normal md:leading-5">
                            Placed in{" "}
                          </span>
                        </p>
                        <p className="text-black md:text-base text-sm font-normal md:leading-5">
                          the U.S.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Version */}
                  <div className="md:hidden block">
                    <h1 className="font-semibold text-[#061A2F] text-2xl text-center leading-7">
                      Be an AI-Ready CMA in 2026
                      <br />
                      <span className="text-lg font-semibold leading-normal bg-[linear-gradient(88deg,_#39F_73.02%,_#FFB801_90.73%)] bg-clip-text text-transparent relative">
                        Earn 50L+
                        <sup className="bg-[linear-gradient(88deg,_#39F_53.02%,_#FFB801_80.73%)] bg-clip-text text-transparent text-sm absolute top-[-3px]">
                          *
                        </sup>{" "}
                        <span> per annum</span>
                      </span>
                    </h1>
                    <div className="mt-3 text-[#061A2F] font-medium text-sm flex flex-col gap-1 leading-tight items-center text-center">
                      <p>2 Exams | 8 Months</p>
                      <p>Jobs at Big 4 & MNCs in India & U.S.</p>
                    </div>
                    {/* Mobile Stats Cards */}
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-[10px]">
                        <p className="text-[#061A2F] text-sm font-normal leading-tight text-center">
                          India's #1
                          <br />
                          <span className="text-[#39F] font-bold text-[18px]">
                            CMA
                          </span>
                          <br />
                          Institute
                        </p>
                      </div>
                      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-[10px]">
                        <p className="text-[#39F] text-[18px] font-bold leading-tight text-center">
                          70K+
                        </p>
                        <p className="text-[#061A2F] text-sm font-normal leading-tight text-center">
                          Global
                          <br />
                          Alumni
                        </p>
                      </div>
                      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-[10px]">
                        <p className="text-[#39F] text-[18px] font-bold leading-tight text-center">
                          1000s
                        </p>
                        <p className="text-[#061A2F] text-sm font-normal leading-tight text-center">
                          Placed in
                          <br />
                          the U.S.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isFeesPage ? (
                <div>
                  {/* Desktop Version */}
                  <div className="hidden md:block">
                    <h1 className=" md:mt-4 md:leading-[44px] font-semibold text-[#061A2F] text-2xl sm:text-[24px] lg:text-[44px] md:text-left text-center leading-6">
                      {title}
                      <br />
                      <span className="md:text-[36px] text-lg font-semibold leading-normal tracking-[-1.44px] bg-[linear-gradient(88deg,_#39F_73.02%,_#FFB801_90.73%)] bg-clip-text text-transparent relative">
                        Earn 50L+
                        <sup className="bg-[linear-gradient(88deg,_#39F_53.02%,_#FFB801_80.73%)] bg-clip-text text-transparent md:text-lg absolute top-[-5px]">
                          *
                        </sup>{" "}
                        per annum
                      </span>
                    </h1>
                    <div className="md:mt-4 mt-2 text-[#061A2F] font-medium text-base flex flex-col mdgap-2 gpa-0 md:leading-[25px] items-center md:items-start text-center">
                      {bulletPoints?.map((point, index) => (
                        <p key={index}>{point}</p>
                      ))}
                    </div>
                    {/* Desktop Stats Cards */}
                    <div className="mt-6 grid grid-cols-3 gap-4">
                      <div className="rounded-[12px] border border-[#E5E5E5] bg-white p-[10px] text-center">
                        <p className="text-black md:text-base text-sm font-normal md:leading-5">
                          India's #1{" "}
                          <span className="text-[#39F] font-bold md:text-[20px]">
                            CPA
                          </span>
                          <br />
                          Institute
                        </p>
                      </div>
                      <div className="rounded-[12px] border border-[#E5E5E5] bg-white p-[10px] text-center">
                        <p className="text-[#39F] md:text-[20px] font-bold md:leading-5">
                          70K+
                        </p>
                        <p className="text-black md:text-base text-sm font-normal md:leading-5">
                          Global Alumni
                        </p>
                      </div>
                      <div className="rounded-[12px] border border-[#E5E5E5] bg-white p-[10px] text-center">
                        <p className="text-[#39F] md:text-[20px] font-bold md:leading-5">
                          1000s{" "}
                          <span className="text-black md:text-base text-sm font-normal md:leading-5">
                            Placed in{" "}
                          </span>
                        </p>
                        <p className="text-black md:text-base text-sm font-normal md:leading-5">
                          the U.S.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Version */}
                  <div className="md:hidden block">
                    <h1 className="font-semibold text-[#061A2F] text-2xl text-center leading-7">
                      {title}
                      <br />
                      <span className="text-lg font-semibold leading-normal bg-[linear-gradient(88deg,_#39F_73.02%,_#FFB801_90.73%)] bg-clip-text text-transparent relative">
                        Earn 50L+
                        <sup className="bg-[linear-gradient(88deg,_#39F_53.02%,_#FFB801_80.73%)] bg-clip-text text-transparent text-sm absolute top-[-3px]">
                          *
                        </sup>{" "}
                        <span> per annum</span>
                      </span>
                    </h1>
                    <div className="mt-3 text-[#061A2F] font-medium text-sm flex flex-col gap-1 leading-tight items-center text-center">
                      {bulletPoints?.map((point, index) => (
                        <p key={index}>{point}</p>
                      ))}
                    </div>
                    {/* Mobile Stats Cards */}
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-[10px]">
                        <p className="text-[#061A2F] text-sm font-normal leading-tight text-center">
                          India's #1
                          <br />
                          <span className="text-[#39F] font-bold text-[18px]">
                            CPA
                          </span>
                          <br />
                          Institute
                        </p>
                      </div>
                      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-[10px]">
                        <p className="text-[#39F] text-[18px] font-bold leading-tight text-center">
                          70K+
                        </p>
                        <p className="text-[#061A2F] text-sm font-normal leading-tight text-center">
                          Global
                          <br />
                          Alumni
                        </p>
                      </div>
                      <div className="rounded-[8px] border border-[#E5E5E5] bg-white p-[10px]">
                        <p className="text-[#39F] text-[18px] font-bold leading-tight text-center">
                          1000s
                        </p>
                        <p className="text-[#061A2F] text-sm font-normal leading-tight text-center">
                          Placed in
                          <br />
                          the U.S.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h1 className=" md:mt-4 md:leading-[44px] font-semibold text-[#061A2F] text-2xl sm:text-[24px] lg:text-[44px] md:text-left text-center leading-6">
                    {title}
                    <br />
                    <span className="md:text-[36px] text-lg font-semibold leading-normal tracking-[-1.44px] bg-[linear-gradient(88deg,_#39F_73.02%,_#FFB801_90.73%)] bg-clip-text text-transparent relative">
                      Earn 50L+
                      <sup className="bg-[linear-gradient(88deg,_#39F_53.02%,_#FFB801_80.73%)] bg-clip-text text-transparent md:text-lg absolute top-[-5px]">
                        *
                      </sup>{" "}
                      per annum
                    </span>
                  </h1>
                  <div className="md:mt-4 mt-2 text-[#061A2F] font-medium text-base flex flex-col mdgap-2 gpa-0 md:leading-[25px] items-center md:items-start text-center">
                    {bulletPoints?.map((point, index) => (
                      <p key={index}>{point}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className={`w-full lg:w-5/12 flex items-center justify-center lg:justify-end relative ${isFeesPage || isCmaLandingPage ? "md:top-[30px]" : "md:top-[-40px]"}`}
            >
              <div
                id="banner-form"
                className="relative w-[420px] rounded-2xl border border-white/80 p-4 sm:p-5"
                style={{
                  borderRadius: "16px",
                  border: "1px solid #FFF",
                  background:
                    "linear-gradient(180deg, rgba(255, 255, 255, 0.20) 0%, rgba(51, 153, 255, 0.20) 100%), #FFF",
                  boxShadow: "0 0 14px 0 rgba(4, 84, 175, 0.29)",
                  backdropFilter: "blur(28px)",
                }}
              >
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                  alt="Decorative stars"
                  width={34}
                  height={38}
                  className="absolute -top-1 -left-1"
                  loading="lazy"
                />
                <EnqueryForm
                  course={pageName}
                  courseId={
                    pageName === "CPA" ? 1 : pageName === "CPA/CMA" ? 1 : 2
                  }
                  formHeading={
                    isFeesPage || isCmaLandingPage
                      ? undefined
                      : FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN
                  }
                  formType="page"
                  isAStepperForm
                  subCategory={
                    pageName === "CPA"
                      ? FinanceAndAccountingSubcategory.CPA
                      : pageName === "CPA/CMA"
                        ? FinanceAndAccountingSubcategory.CPA
                        : FinanceAndAccountingSubcategory.CMA
                  }
                  submitButtonName={
                    isFeesPage
                      ? "Enquire Now"
                      : isWebinarPage
                        ? "Join Webinar"
                        : "Request a call back"
                  }
                  successMessage={SuccessMessageType.ACCOUNTING}
                  verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                  darkBackground={false}
                  isWebinarPage={isWebinarPage}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Modal */}
      {isFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-full max-w-[430px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModelClose}
              className="text-white text-2xl absolute right-3 top-2 cursor-pointer"
            >
              ✕
            </button>
            <EnqueryForm
              course={pageName}
              courseId={
                pageName === "CPA"
                  ? 1
                  : pageName === "CMA"
                    ? 2
                    : pageName === "USP"
                      ? 3
                      : 1
              }
              formHeading={FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN}
              formType="page"
              isAStepperForm
              subCategory={
                pageName === "CPA"
                  ? FinanceAndAccountingSubcategory.CPA
                  : pageName === "CMA"
                    ? FinanceAndAccountingSubcategory.CMA
                    : pageName === "USP"
                      ? FinanceAndAccountingSubcategory.USP
                      : FinanceAndAccountingSubcategory.CPA
              }
              submitButtonName="Request a call back"
              successMessage={SuccessMessageType.ACCOUNTING}
              verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
              darkBackground={true}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VisitPageBannerNew;
